// useDensity — 밀도 모드 상태 관리 훅.
// - localStorage에서 초기값 복원, 없으면 기기 너비 기반 자동 결정
// - setDensity: localStorage 저장 + document.documentElement data-density 즉시 반영
// - 마운트 시 초기 data-density도 동기화

import { useState, useCallback } from 'react';
import {
  type DensityMode,
  DENSITY_STORAGE_KEY,
  DENSITY_DEFAULT,
  DENSITY_MOBILE_DEFAULT,
  DENSITY_MOBILE_BREAKPOINT,
} from '../utils/density';

// localStorage에서 밀도 값 읽기. 저장값이 없거나 유효하지 않으면 null 반환.
function readStoredDensity(): DensityMode | null {
  try {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (stored === 'compact' || stored === 'default' || stored === 'comfortable') {
      return stored;
    }
  } catch {
    // localStorage 접근 불가 환경 (SSR, 시크릿 모드 차단 등) — 무시
  }
  return null;
}

// 기기 너비에 따른 기본 밀도 결정.
// 사용자 저장값이 없을 때만 호출한다.
function detectDefaultDensity(): DensityMode {
  if (typeof window !== 'undefined' && window.innerWidth < DENSITY_MOBILE_BREAKPOINT) {
    return DENSITY_MOBILE_DEFAULT;
  }
  return DENSITY_DEFAULT;
}

// document.documentElement에 data-density 속성을 적용한다.
function applyDensityToDOM(density: DensityMode): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-density', density);
  }
}

// 초기 밀도 결정: 저장값 → 기기 자동 감지 순으로.
function resolveInitialDensity(): DensityMode {
  const stored = readStoredDensity();
  return stored ?? detectDefaultDensity();
}

export interface UseDensityReturn {
  density: DensityMode;
  setDensity: (value: DensityMode) => void;
}

export function useDensity(): UseDensityReturn {
  const [density, setDensityState] = useState<DensityMode>(() => {
    const initial = resolveInitialDensity();
    // 초기화 시점에 DOM에도 즉시 적용
    applyDensityToDOM(initial);
    return initial;
  });

  const setDensity = useCallback((value: DensityMode) => {
    // localStorage에 사용자 명시 선택값 저장
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, value);
    } catch {
      // 저장 실패해도 UI 상태·DOM 속성은 반영
    }
    // 즉시 DOM 반영 — 모든 [data-density] 토큰 자동 갱신
    applyDensityToDOM(value);
    setDensityState(value);
  }, []);

  return { density, setDensity };
}
